import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { canRead } from '@/lib/permissions';
import type { BadgeProps } from '@/components/ui/Badge';
import type { SalesChannelKind } from '@/types/database';

// -----------------------------------------------------------------------
// Returns / RMA (migration 110)
//
// Two independent tracks per RMA:
//   status             physical: requested → authorized → in_transit →
//                      partially_received → received → closed
//   resolution_status  money / goods back: pending → issued
// Channels disagree on ordering (Amazon refunds before the unit arrives,
// wholesale credits wait for inspection), so neither track gates the other
// except that closing requires a settled resolution.
// -----------------------------------------------------------------------

type BadgeVariant = NonNullable<BadgeProps['variant']>;

export const RETURN_STATUSES = [
  'requested',
  'authorized',
  'in_transit',
  'partially_received',
  'received',
  'closed',
  'rejected',
  'cancelled',
] as const;
export type ReturnStatus = (typeof RETURN_STATUSES)[number];

export const RETURN_STATUS_LABELS: Record<ReturnStatus, string> = {
  requested: 'Requested',
  authorized: 'Authorized',
  in_transit: 'In transit',
  partially_received: 'Partially received',
  received: 'Received',
  closed: 'Closed',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

export const RETURN_STATUS_VARIANTS: Record<ReturnStatus, BadgeVariant> = {
  requested: 'warning',
  authorized: 'secondary',
  in_transit: 'info',
  partially_received: 'warning',
  received: 'info',
  closed: 'success',
  rejected: 'muted',
  cancelled: 'muted',
};

export const RETURN_CLOSED_STATUSES: ReturnStatus[] = ['closed', 'rejected', 'cancelled'];
export const RETURN_RECEIVABLE_STATUSES: ReturnStatus[] = ['authorized', 'in_transit', 'partially_received'];

export const RETURN_REASONS = [
  'damaged_in_transit',
  'defective',
  'wrong_item',
  'not_as_described',
  'missing_parts',
  'no_longer_needed',
  'ordered_by_mistake',
  'arrived_late',
  'warranty',
  'overstock',
  'other',
] as const;
export type ReturnReason = (typeof RETURN_REASONS)[number];

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  damaged_in_transit: 'Damaged in transit',
  defective: 'Defective',
  wrong_item: 'Wrong item sent',
  not_as_described: 'Not as described',
  missing_parts: 'Missing parts',
  no_longer_needed: 'No longer needed',
  ordered_by_mistake: 'Ordered by mistake',
  arrived_late: 'Arrived too late',
  warranty: 'Warranty claim',
  overstock: 'Overstock (wholesale)',
  other: 'Other',
};

export const RECEIPT_MODES = ['ship_to_us', 'channel_warehouse', 'no_return'] as const;
export type ReceiptMode = (typeof RECEIPT_MODES)[number];

export const RECEIPT_MODE_LABELS: Record<ReceiptMode, string> = {
  ship_to_us: 'Ships back to us',
  channel_warehouse: 'Channel warehouse (Amazon FBA)',
  no_return: 'No return (returnless refund)',
};

export const RESOLUTIONS = [
  'refund',
  'store_credit',
  'credit_memo',
  'replacement',
  'exchange',
  'repair',
  'none',
] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

export const RESOLUTION_LABELS: Record<Resolution, string> = {
  refund: 'Refund',
  store_credit: 'Store credit',
  credit_memo: 'Credit memo',
  replacement: 'Replacement',
  exchange: 'Exchange',
  repair: 'Repair',
  none: 'No resolution',
};

export const RESOLUTION_STATUSES = ['pending', 'issued', 'declined', 'not_applicable'] as const;
export type ResolutionStatus = (typeof RESOLUTION_STATUSES)[number];

export const RESOLUTION_STATUS_LABELS: Record<ResolutionStatus, string> = {
  pending: 'Pending',
  issued: 'Issued',
  declined: 'Declined',
  not_applicable: 'Not applicable',
};

export const RESOLUTION_STATUS_VARIANTS: Record<ResolutionStatus, BadgeVariant> = {
  pending: 'warning',
  issued: 'success',
  declined: 'muted',
  not_applicable: 'muted',
};

export const DISPOSITIONS = ['restock', 'inspect', 'scrap', 'return_to_vendor'] as const;
export type Disposition = (typeof DISPOSITIONS)[number];

export const DISPOSITION_LABELS: Record<Disposition, string> = {
  restock: 'Restock (sellable)',
  inspect: 'Hold for inspection',
  scrap: 'Scrap',
  return_to_vendor: 'Return to vendor',
};

export const ITEM_CONDITIONS = ['new', 'like_new', 'opened', 'damaged', 'defective', 'missing_parts'] as const;
export type ItemCondition = (typeof ITEM_CONDITIONS)[number];

export const ITEM_CONDITION_LABELS: Record<ItemCondition, string> = {
  new: 'New / sealed',
  like_new: 'Like new',
  opened: 'Opened',
  damaged: 'Damaged',
  defective: 'Defective',
  missing_parts: 'Missing parts',
};

export const LABEL_PAYERS = ['us', 'customer', 'channel'] as const;
export type LabelPayer = (typeof LABEL_PAYERS)[number];

export const LABEL_PAYER_LABELS: Record<LabelPayer, string> = {
  us: 'Virro',
  customer: 'Customer',
  channel: 'Channel (e.g. Amazon prepaid)',
};

// -----------------------------------------------------------------------
// Channel profiles
//
// Each channel's default receipt mode, allowed resolutions, and who
// actually moves the money. The RMA form seeds itself from these; every
// field stays editable per RMA.
// -----------------------------------------------------------------------

export interface ChannelReturnProfile {
  label: string;
  receiptModes: ReceiptMode[];
  defaultReceiptMode: ReceiptMode;
  resolutions: Resolution[];
  defaultResolution: Resolution;
  defaultLabelPaidBy: LabelPayer | null;
  /** Where the refund / credit is actually executed. */
  resolutionVenue: string;
  /** Standard return window in days from delivery, when the channel sets one. */
  windowDays: number | null;
}

export const CHANNEL_RETURN_PROFILES: Record<SalesChannelKind, ChannelReturnProfile> = {
  shopify: {
    label: 'Shopify',
    receiptModes: ['ship_to_us', 'no_return'],
    defaultReceiptMode: 'ship_to_us',
    resolutions: ['refund', 'store_credit', 'exchange', 'replacement'],
    defaultResolution: 'refund',
    defaultLabelPaidBy: 'us',
    resolutionVenue: 'Shopify admin (refund to original payment)',
    windowDays: 30,
  },
  amazon: {
    label: 'Amazon',
    receiptModes: ['ship_to_us', 'channel_warehouse', 'no_return'],
    defaultReceiptMode: 'ship_to_us',
    resolutions: ['refund', 'replacement'],
    defaultResolution: 'refund',
    defaultLabelPaidBy: 'channel',
    resolutionVenue: 'Seller Central (Amazon issues FBA refunds automatically)',
    windowDays: 30,
  },
  b2b_portal: {
    label: 'Wholesale',
    receiptModes: ['ship_to_us', 'no_return'],
    defaultReceiptMode: 'ship_to_us',
    resolutions: ['credit_memo', 'replacement', 'repair', 'refund'],
    defaultResolution: 'credit_memo',
    defaultLabelPaidBy: 'customer',
    resolutionVenue: 'QuickBooks credit memo against the customer account',
    windowDays: null,
  },
  manual: {
    label: 'Other / direct',
    receiptModes: ['ship_to_us', 'no_return'],
    defaultReceiptMode: 'ship_to_us',
    resolutions: ['refund', 'store_credit', 'credit_memo', 'replacement', 'exchange', 'repair', 'none'],
    defaultResolution: 'refund',
    defaultLabelPaidBy: null,
    resolutionVenue: 'Wherever the original payment was taken',
    windowDays: null,
  },
};

export const RETURN_CHANNEL_ORDER: SalesChannelKind[] = ['shopify', 'amazon', 'b2b_portal', 'manual'];

// -----------------------------------------------------------------------
// Rows
// -----------------------------------------------------------------------

export interface ReturnLine {
  id: string;
  return_id: string;
  item_id: string | null;
  sku: string | null;
  description: string | null;
  order_line_ref: string | null;
  quantity: number;
  received_quantity: number;
  unit_price_cents: number | null;
  reason_code: ReturnReason | null;
  disposition: Disposition | null;
  notes: string | null;
  position: number;
  items: {
    id: string;
    sku: string;
    name: string;
    base_uom: string;
    is_lot_tracked: boolean;
  } | null;
}

export interface ReturnReceiptLine {
  id: string;
  return_line_id: string;
  item_id: string | null;
  quantity: number;
  disposition: Disposition;
  condition: ItemCondition | null;
  received_at: string;
  notes: string | null;
  locations: { id: string; name: string; code: string } | null;
  lots: { lot_number: string } | null;
}

export interface ReturnRow {
  id: string;
  organization_id: string;
  rma_number: string;
  status: ReturnStatus;
  channel_kind: SalesChannelKind;
  sales_channel_id: string | null;
  sales_order_id: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_email: string | null;
  external_return_id: string | null;
  external_order_ref: string | null;
  reason_code: ReturnReason;
  reason_detail: string | null;
  receipt_mode: ReceiptMode;
  resolution: Resolution;
  resolution_status: ResolutionStatus;
  resolution_reference: string | null;
  resolution_issued_at: string | null;
  refund_cents: number | null;
  restocking_fee_cents: number;
  label_paid_by: LabelPayer | null;
  carrier: string | null;
  tracking_number: string | null;
  requested_at: string;
  authorized_at: string | null;
  received_at: string | null;
  resolved_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  sales_orders: { id: string; so_number: string; ordered_at: string; total_cents: number } | null;
  sales_channels: { id: string; name: string; kind: SalesChannelKind } | null;
  customers: { id: string; name: string } | null;
  return_lines: ReturnLine[];
  return_receipt_lines: ReturnReceiptLine[];
}

const RETURN_SELECT = `*,
  sales_orders:sales_order_id(id, so_number, ordered_at, total_cents),
  sales_channels:sales_channel_id(id, name, kind),
  customers:customer_id(id, name),
  return_lines!return_lines_return_id_fkey(*, items(id, sku, name, base_uom, is_lot_tracked)),
  return_receipt_lines!return_receipt_lines_return_id_fkey(id, return_line_id, item_id, quantity, disposition, condition, received_at, notes,
    locations:to_location_id(id, name, code), lots(lot_number))`;

const RETURNS_KEY = ['returns'];

/** Expected value of everything on the RMA at the price the customer paid. */
export function returnLinesValueCents(r: Pick<ReturnRow, 'return_lines'>): number {
  return r.return_lines.reduce((acc, l) => acc + (l.unit_price_cents ?? 0) * l.quantity, 0);
}

export function returnDisplayCustomer(r: ReturnRow): string {
  return r.customers?.name ?? r.customer_name ?? r.customer_email ?? '—';
}

export function returnLineLabel(l: Pick<ReturnLine, 'items' | 'sku' | 'description'>): string {
  return l.items?.name ?? l.description ?? l.sku ?? 'Unmapped item';
}

export function useReturns() {
  const { role, profile, loading } = useAuth();
  const enabled = !loading && canRead(role, 'sales', profile?.section_access);
  return useQuery({
    queryKey: RETURNS_KEY,
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('returns' as never)
        .select(RETURN_SELECT)
        .order('requested_at', { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data as unknown as ReturnRow[]).map((r) => ({
        ...r,
        return_lines: [...(r.return_lines ?? [])].sort((a, b) => a.position - b.position),
        return_receipt_lines: [...(r.return_receipt_lines ?? [])].sort((a, b) =>
          b.received_at.localeCompare(a.received_at),
        ),
      }));
    },
  });
}

export interface ReturnCustomer {
  id: string;
  name: string;
  email: string | null;
}

/**
 * Customer directory for wholesale RMAs. Reads through `customers_read_org`
 * directly so it isn't gated behind financials like `useCustomers`.
 */
export function useReturnCustomers() {
  return useQuery({
    queryKey: ['return-customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, email')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return data as ReturnCustomer[];
    },
  });
}

// -----------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------

function invalidateReturns(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: RETURNS_KEY });
}

export interface SaveReturnLineInput {
  id?: string | null;
  item_id?: string | null;
  sku?: string | null;
  description?: string | null;
  order_line_ref?: string | null;
  quantity: number;
  unit_price_cents?: number | null;
  reason_code?: ReturnReason | null;
  disposition?: Disposition | null;
  notes?: string | null;
}

export interface SaveReturnInput {
  id?: string | null;
  status?: ReturnStatus;
  sales_order_id?: string | null;
  sales_channel_id?: string | null;
  channel_kind: SalesChannelKind;
  customer_id?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  external_return_id?: string | null;
  external_order_ref?: string | null;
  reason_code: ReturnReason;
  reason_detail?: string | null;
  receipt_mode: ReceiptMode;
  resolution: Resolution;
  label_paid_by?: LabelPayer | null;
  carrier?: string | null;
  tracking_number?: string | null;
  restocking_fee_cents?: number | null;
  notes?: string | null;
  lines: SaveReturnLineInput[];
}

/** Create or update an RMA and its lines in one transaction (`save_return`). */
export function useSaveReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveReturnInput) => {
      const { lines, ...header } = input;
      const { data, error } = await supabase.rpc('save_return' as never, {
        p_return: header,
        p_lines: lines,
      } as never);
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => invalidateReturns(qc),
  });
}

export interface UpdateReturnInput {
  id: string;
  status?: ReturnStatus;
  resolution?: Resolution;
  resolution_status?: ResolutionStatus;
  refund_cents?: number | null;
  resolution_reference?: string | null;
  carrier?: string | null;
  tracking_number?: string | null;
}

/**
 * Status transitions and resolution bookkeeping. The DB trigger enforces
 * the rules (received only via receiving, close needs a settled resolution).
 */
export function useUpdateReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateReturnInput) => {
      const { error } = await supabase
        .from('returns' as never)
        .update(patch as never)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => invalidateReturns(qc),
  });
}

export interface ReceiveReturnLineInput {
  return_line_id: string;
  quantity: number;
  disposition: Disposition;
  condition?: ItemCondition | null;
  to_location_id?: string | null;
  lot_number?: string | null;
  notes?: string | null;
}

/**
 * Post what came back via `receive_return`. Restock / inspect /
 * return-to-vendor lines add on-hand at the chosen location as a Receipt
 * movement; scrap records the receipt without adding stock.
 */
export function useReceiveReturn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      returnId: string;
      receivedAt: string;
      notes?: string | null;
      lines: ReceiveReturnLineInput[];
    }) => {
      const { data, error } = await supabase.rpc('receive_return' as never, {
        p_return_id: input.returnId,
        p_received_at: input.receivedAt,
        p_notes: input.notes ?? null,
        p_lines: input.lines,
      } as never);
      if (error) throw error;
      return data as unknown as number;
    },
    onSuccess: () => {
      invalidateReturns(qc);
      qc.invalidateQueries({ queryKey: ['stock-movements'] });
      qc.invalidateQueries({ queryKey: ['inventory-levels'] });
      qc.invalidateQueries({ queryKey: ['lots'] });
    },
  });
}
